import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { specRequirementsApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  SpecCharterInput,
  SpecFlowInput,
  SpecFeatureRow,
  SpecIntegrationTestRow,
  SpecValidationRow,
  SpecDecisionRow,
  SpecDesignLink,
  SpecFindingResolutionInput,
  SpecNarrativeInput,
  SpecPlArtifactSubmitInput,
  SpecPlReviewSubmitInput,
  SpecReadLocale,
  SpecReviewSubmitInput,
} from '../lib/api/spec-requirements.js';

/**
 * i18next の言語文字列を、成果物を読む言語へ正規化する。
 *
 * **`vi` を `en` に畳まない。** バグ評価などの `useApiLocale` は ja/en の 2 値に畳んでいるが、
 * 要件定義の成果物はベトナム語の訳を実際に保存しているので、畳むとベトナム語話者が英語を読む。
 *
 * 知らない言語は原文（日本語）に寄せる。訳を持たない言語で読ませるより、原文の方が確かに読める。
 *
 * フックと分けてあるのは、判定だけを素で確かめられるようにするため（spec-console の
 * `resolveSpecLocale` と同じ分け方）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 画面の言語を要件定義の成果物を読む言語へ正規化する（ベトナム語を英語に畳まない）
 * @graph-connects none
 */
export function resolveSpecReadLocale(lang: string | undefined): SpecReadLocale {
  const value = lang ?? 'ja';
  if (value.startsWith('en')) return 'en';
  if (value.startsWith('vi')) return 'vi';
  return 'ja';
}

/**
 * 画面の言語を、成果物を読む言語として返す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 画面が描かれている言語を要件定義の成果物を読む言語として供給する
 * @graph-connects resolveSpecReadLocale [calls] 言語文字列の正規化
 */
export function useSpecReadLocale(): SpecReadLocale {
  const { i18n } = useTranslation();
  return resolveSpecReadLocale(i18n.language);
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義の成果物がある案件の一覧を取得する
 * @graph-connects /spec-requirements [calls] 案件一覧取得API
 * @graph-connects specRequirementsApi.list [calls] 案件一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSpecProjects(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.specRequirements.list(),
    queryFn: () => specRequirementsApi.list(),
    enabled: options.enabled ?? true,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件 1 件の要件定義の成果物（ナラティブ・業務フロー・レビュー状態）を取得する
 * @graph-connects /spec-requirements [calls] 案件の成果物取得API
 * @graph-connects specRequirementsApi.detail [calls] 成果物APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSpecProjectDetail(pjKey: string) {
  const locale = useSpecReadLocale();
  return useQuery({
    queryKey: queryKeys.specRequirements.detail(pjKey, locale),
    queryFn: () => specRequirementsApi.detail(pjKey, locale),
    enabled: pjKey.length > 0,
  });
}

/**
 * ナラティブの原文（`ja`）だけを編集フォームの初期値用に取得する。
 *
 * `useSpecProjectDetail` は画面の言語で読むため、訳が fresh なら訳文が返る。編集フォームの
 * 初期値にそれを使うと、保存時に訳文がそのまま新しい原文として上書きされてしまう。
 *
 * **読む言語はその成果物の `sourceLocale`**（レスポンスが返す）。`ja` 決め打ちにすると、原文が
 * 英語の成果物では日本語の訳を編集して保存し、英語の原文を日本語で上書きすることになる。
 *
 * 画面の言語がその成果物の原文と同じときは `useSpecProjectDetail` と同じキャッシュを指すため、
 * 追加のリクエストは発生しない。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ナラティブ編集フォームの初期値として、画面言語に関わらずその成果物の原文を取得する
 * @graph-connects /spec-requirements [calls] 案件の成果物取得API（その成果物の原文の言語で読む）
 * @graph-connects specRequirementsApi.detail [calls] 成果物APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSpecNarrativeSource(
  pjKey: string,
  enabled: boolean,
  sourceLocale: SpecReadLocale,
) {
  return useQuery({
    queryKey: queryKeys.specRequirements.detail(pjKey, sourceLocale),
    queryFn: () => specRequirementsApi.detail(pjKey, sourceLocale),
    enabled: enabled && pjKey.length > 0,
    select: (data) => data.narrative,
  });
}

/**
 * 編集フォーム用に、業務フロー 1 本を**その成果物の原文の言語で**読む。
 *
 * 一覧の表示は画面の言語で読むが、編集フォームに渡すのは原文でなければならない。訳された図を
 * そのままフォームに載せると、保存した瞬間に**原文の図が訳で上書きされる**（原文が失われ、
 * 他の言語の訳も原文として訳の対象になる）。ナラティブで同じ事故を踏んだので、図でも同じ形で
 * 原文に固定する。
 *
 * 読む先は同じ詳細 API なので、画面言語ぶんのキャッシュとは別キーで保持される。
 * @graph-connects specRequirementsApi [calls] 要件定義 API クライアント
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSpecFlowSource(
  pjKey: string,
  flowId: string,
  enabled: boolean,
  sourceLocale: SpecReadLocale,
) {
  return useQuery({
    queryKey: queryKeys.specRequirements.detail(pjKey, sourceLocale),
    queryFn: () => specRequirementsApi.detail(pjKey, sourceLocale),
    enabled: enabled && pjKey.length > 0 && flowId.length > 0,
    select: (data) => data.flows.find((flow) => flow.flowId === flowId),
  });
}

/**
 * 案件の成果物とレビュー状態を読み直す。
 *
 * レビューの操作はどれも版と状態を動かすので、成功したら同じものを無効化する。
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
function useRefreshSpec(pjKey: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    // 保存は原文も訳も動かすので、全言語ぶんを無効化する
    await queryClient.invalidateQueries({
      queryKey: queryKeys.specRequirements.detailAll(pjKey),
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.specRequirements.list() });
    await queryClient.invalidateQueries({ queryKey: queryKeys.specRequirements.nextStep(pjKey) });
    // レビュー待ちの横断一覧も動く。AI 待ち・DLE 待ちの両方を前方一致でまとめて捨てる
  };
}

/**
 * ナラティブを保存する。
 *
 * 保存の可否（担当者かどうか・章ごとの行数上限）は product-api が判定する。画面は同じ口を
 * 叩くだけで、MCP から書いたときと規則が枝分かれしないようにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ナラティブを product-api 経由で保存し、案件の成果物を再取得する
 * @graph-connects /spec-requirements [calls] ナラティブ保存API
 * @graph-connects specRequirementsApi.updateNarrative [calls] ナラティブ保存APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useUpdateSpecNarrative(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  const readLocale = useSpecReadLocale();
  return useMutation({
    // **画面の言語を原文の言語として送る。これは新規作成のときだけ効く。** 既にある成果物では
    // API が保存済みの値を守るので、英語の画面から保存しても原文の言語は変わらない。
    // 呼び出し側が明示的に渡したときはそれを尊重する
    mutationFn: (data: SpecNarrativeInput) =>
      specRequirementsApi.updateNarrative(pjKey, {
        sourceLocale: readLocale,
        ...data,
      }),
    onSuccess: refresh,
  });
}

/**
 * 次の 1 手を取得する。
 *
 * 保存のたびに変わるので、成果物を invalidate するときに一緒に読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の要件定義で次にやること 1 つを取得する
 * @graph-connects /spec-requirements [calls] 次の 1 手取得API
 * @graph-connects specRequirementsApi.nextStep [calls] 次の 1 手APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSpecNextStep(pjKey: string) {
  return useQuery({
    queryKey: queryKeys.specRequirements.nextStep(pjKey),
    queryFn: () => specRequirementsApi.nextStep(pjKey),
    enabled: pjKey.length > 0,
  });
}

/**
 * 成果物のフォーマット（書き方のルール）を取得する。
 *
 * 保存されるものではないので、取ったら変わらない（`staleTime` を長く取る）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 成果物の書き方（必須要素・省く要素・サンプル）を取得する
 * @graph-connects /spec-requirements [calls] フォーマット取得API
 * @graph-connects specRequirementsApi.format [calls] フォーマットAPIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSpecFormat(artifact: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.specRequirements.format(artifact),
    queryFn: () => specRequirementsApi.format(artifact),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 業務フローのレビューを今の版に固定して依頼する
 * @graph-connects /spec-requirements [calls] レビュー依頼API
 * @graph-connects specRequirementsApi.requestReview [calls] レビュー依頼APIクライアント呼び出し
 */
export function useRequestSpecReview(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (targetId: string) => specRequirementsApi.requestReview(pjKey, targetId),
    onSuccess: refresh,
  });
}

/**
 * レビュー結果を提出する。
 *
 * 画面から出すのは DLE の承認・差し戻し。AI レビューは 5 観点を通した結果をまとめて出すもので
 * 人が画面で作るものではないが、口は同じなので `reviewer` で分かれるだけ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビュー結果（DLE の承認・差し戻し）を提出する
 * @graph-connects /spec-requirements [calls] レビュー提出API
 * @graph-connects specRequirementsApi.submitReview [calls] レビュー提出APIクライアント呼び出し
 */
export function useSubmitSpecReview(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: ({ targetId, input }: { targetId: string; input: SpecReviewSubmitInput }) =>
      specRequirementsApi.submitReview(pjKey, targetId, input),
    onSuccess: refresh,
  });
}

/**
 * DLE の承認を取り消し、DLE レビュー待ちに戻す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 押し間違いの DLE 承認を、依頼し直さずに取り消す
 * @graph-connects /spec-requirements [calls] DLE 承認の取り消しAPI
 * @graph-connects specRequirementsApi.revokeApproval [calls] DLE 承認の取り消しAPIクライアント呼び出し
 */
export function useRevokeSpecApproval(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (targetId: string) => specRequirementsApi.revokeApproval(pjKey, targetId),
    onSuccess: refresh,
  });
}

/**
 * 指摘を解決する。
 *
 * まとめて渡す（1 件でも不正なら何も記録されない）。塞ぐ指摘が全て片付くと API 側で承認へ戻る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 指摘を直した／見送った／取り消したとして記録する
 * @graph-connects /spec-requirements [calls] 指摘の解決API
 * @graph-connects specRequirementsApi.resolveFindings [calls] 指摘の解決APIクライアント呼び出し
 */
export function useResolveSpecFindings(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: ({
      targetId,
      resolutions,
    }: {
      targetId: string;
      resolutions: SpecFindingResolutionInput[];
    }) => specRequirementsApi.resolveFindings(pjKey, targetId, resolutions),
    onSuccess: refresh,
  });
}

/**
 * 憲章を部分更新する。
 *
 * 渡した欄だけが上書きされるので、画面は触った欄だけを送る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロジェクト憲章を product-api 経由で部分更新する
 * @graph-connects /spec-requirements [calls] 憲章保存API
 * @graph-connects specRequirementsApi.updateCharter [calls] 憲章保存APIクライアント呼び出し
 */
export function useUpdateSpecCharter(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (data: Partial<SpecCharterInput>) => specRequirementsApi.updateCharter(pjKey, data),
    onSuccess: refresh,
  });
}

/**
 * 機能一覧を保存する。
 *
 * 列ごとの文字数上限・行数上限・備考の未決・ASIS=TOBE の判定は API が持つ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 機能一覧を product-api 経由で保存する
 * @graph-connects /spec-requirements [calls] 機能一覧保存API
 * @graph-connects specRequirementsApi.updateFeatures [calls] 機能一覧保存APIクライアント呼び出し
 */
export function useUpdateSpecFeatures(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (rows: SpecFeatureRow[]) => specRequirementsApi.updateFeatures(pjKey, rows),
    onSuccess: refresh,
  });
}

/**
 * 総合テストを保存する。
 *
 * 列ごとの上限・1 セルに 2 つ詰め・覆うところと場面が実在するか、の判定は API が持つ。
 * 覆えていないところは保存を止めず、結果の `warnings` で返る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 総合テストを product-api 経由で保存する
 * @graph-connects /spec-requirements [calls] 総合テスト保存API
 * @graph-connects specRequirementsApi.updateIntegrationTest [calls] 総合テスト保存APIクライアント呼び出し
 */
export function useUpdateSpecIntegrationTest(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (rows: SpecIntegrationTestRow[]) =>
      specRequirementsApi.updateIntegrationTest(pjKey, rows),
    onSuccess: refresh,
  });
}

/**
 * バリデーション一覧を保存する。
 *
 * 列ごとの上限・未決の印・記入例の行の判定は API が持つ。内部名などの書き方は保存を止めず、
 * 結果の `warnings` で返る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バリデーション一覧を product-api 経由で保存する
 * @graph-connects /spec-requirements [calls] バリデーション一覧保存API
 * @graph-connects specRequirementsApi.updateValidationList [calls] バリデーション一覧保存APIクライアント呼び出し
 */
export function useUpdateSpecValidationList(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (rows: SpecValidationRow[]) =>
      specRequirementsApi.updateValidationList(pjKey, rows),
    onSuccess: refresh,
  });
}

/**
 * 決定記録を保存する。
 *
 * 列ごとの上限・未決の印・記入例の行の判定は API が持つ。捨てた案が無い行（選ばなかった案が
 * 「なし」）は保存を止めず、結果の `warnings` で返る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 決定記録を product-api 経由で保存する
 * @graph-connects /spec-requirements [calls] 決定記録保存API
 * @graph-connects specRequirementsApi.updateDecisionLog [calls] 決定記録保存APIクライアント呼び出し
 */
export function useUpdateSpecDecisionLog(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (rows: SpecDecisionRow[]) => specRequirementsApi.updateDecisionLog(pjKey, rows),
    onSuccess: refresh,
  });
}

/**
 * 補足欄（markdown の自由欄）を保存する。
 *
 * 空欄・文字数上限の判定は API が持つ。上限に近づいたことは保存を止めず、結果の `warnings` で返る。
 * 行数は縛らない（自由欄には「表の行」という概念が無い）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 補足欄を product-api 経由で保存する
 * @graph-connects /spec-requirements [calls] 補足欄保存API
 * @graph-connects specRequirementsApi.updateProjectNotes [calls] 補足欄保存APIクライアント呼び出し
 */
export function useUpdateSpecProjectNotes(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (body: string) => specRequirementsApi.updateProjectNotes(pjKey, body),
    onSuccess: refresh,
  });
}

/**
 * デザイン成果物（名前と URL の組）を保存する。
 *
 * 件数の上限・URL のスキーム・名前の長さの判定は API が持つ。上限に近づいたことは保存を止めず、
 * 結果の `warnings` で返る。**書ける人も API が決める**（担当者に加えて Interface Design チーム）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン成果物を product-api 経由で保存する
 * @graph-connects /spec-requirements [calls] デザイン成果物保存API
 * @graph-connects specRequirementsApi.updateDesignLinks [calls] デザイン成果物保存APIクライアント呼び出し
 */
export function useUpdateSpecDesignLinks(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (links: SpecDesignLink[]) => specRequirementsApi.updateDesignLinks(pjKey, links),
    onSuccess: refresh,
  });
}

/**
 * 業務フローを保存する。
 *
 * 図の形（開始・終了・レーン・ID・Phase・new/existing・行き止まり・到達性・文字数）の
 * 判定は API が持つ。画面は保存前に同じ判定を `checkFlow` で呼べる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 業務フロー（Mermaid・名前・前後の接続）を product-api 経由で保存する
 * @graph-connects /spec-requirements [calls] 業務フロー保存API
 * @graph-connects specRequirementsApi.updateFlow [calls] 業務フロー保存APIクライアント呼び出し
 */
export function useUpdateSpecFlow(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: ({ flowId, input }: { flowId: string; input: SpecFlowInput }) =>
      specRequirementsApi.updateFlow(pjKey, flowId, input),
    onSuccess: refresh,
  });
}

/**
 * 業務フローを取り下げる。削除はせず、有効な要件から外してレビューを閉じる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 業務フローを理由つきで取り下げ、案件の成果物を読み直す
 * @graph-connects /spec-requirements [calls] 業務フロー取り下げAPI
 * @graph-connects specRequirementsApi.retireFlow [calls] 取り下げAPIクライアント呼び出し
 */
export function useRetireSpecFlow(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: ({ flowId, reason }: { flowId: string; reason: string }) =>
      specRequirementsApi.retireFlow(pjKey, flowId, reason),
    onSuccess: refresh,
  });
}

/**
 * 業務フローの取り下げを解除する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 業務フローの取り下げを解除し、案件の成果物を読み直す
 * @graph-connects /spec-requirements [calls] 業務フロー取り下げ解除API
 * @graph-connects specRequirementsApi.restoreFlow [calls] 解除APIクライアント呼び出し
 */
export function useRestoreSpecFlow(pjKey: string) {
  const refresh = useRefreshSpec(pjKey);
  return useMutation({
    mutationFn: (flowId: string) => specRequirementsApi.restoreFlow(pjKey, flowId),
    onSuccess: refresh,
  });
}

/**
 * 業務フローを保存せずに機械チェックする。
 *
 * 保存しないので案件の成果物は読み直さない（`onSuccess` で invalidate しない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 業務フローを保存せずに機械チェックする
 * @graph-connects /spec-requirements [calls] 業務フロー機械チェックAPI
 * @graph-connects specRequirementsApi.checkFlow [calls] 機械チェックAPIクライアント呼び出し
 */
export function useCheckSpecFlow() {
  return useMutation({ mutationFn: specRequirementsApi.checkFlow });
}

/**
 * レビュー 1 件の出来事（依頼・提出・指摘の解決・DLE 承認の取り消し・取り下げ）を古い順に取得する。
 *
 * **開いたときだけ引く**（`enabled`）。案件の詳細は成果物 6 つぶんのレビューを持つので、
 * 画面を開いた時点で全ての経緯を引くと、読まれない取得が 6 回走る。
 *
 * 誰がいつ何をしたかは出来事の記録にしか無い。レビュー本体は操作のたびに上書きされるので、
 * 「誰が直したと記録したか」「何回差し戻されたか」はここから読む。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビューの差し戻しと解決の経緯を、開いた成果物についてだけ取得する
 * @graph-connects /spec-requirements [calls] レビューの出来事取得API
 * @graph-connects specRequirementsApi.reviewEvents [calls] 出来事取得APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSpecReviewEvents(pjKey: string, targetId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.specRequirements.reviewEvents(pjKey, targetId),
    queryFn: () => specRequirementsApi.reviewEvents(pjKey, targetId),
    enabled: enabled && pjKey.length > 0 && targetId.length > 0,
  });
}

/**
 * 案件全体の PL レビュー（最後の判断と履歴）を取得する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件全体に対する PL の承認・差し戻しの状況を取得する
 * @graph-connects /spec-requirements [calls] PL レビュー取得API
 * @graph-connects specRequirementsApi.getPlReview [calls] PL レビュー取得APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSpecPlReview(pjKey: string) {
  return useQuery({
    queryKey: queryKeys.specRequirements.plReview(pjKey),
    queryFn: () => specRequirementsApi.getPlReview(pjKey),
    enabled: pjKey.length > 0,
  });
}

/**
 * 案件全体を PL が承認する／コメントを付けて差し戻す。
 *
 * PL かどうか・コメントの必須や上限は API が判定する。画面は返ったメッセージを見せるだけ。
 * 判断は次にやること（承認待ちか）にも効くので、案件の詳細と一緒に読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件全体に対する PL の承認・差し戻しを記録する
 * @graph-connects /spec-requirements [calls] PL レビュー提出API
 * @graph-connects specRequirementsApi.submitPlReview [calls] PL レビュー提出APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSubmitSpecPlReview(pjKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SpecPlReviewSubmitInput) =>
      specRequirementsApi.submitPlReview(pjKey, input),
    onSuccess: async (review) => {
      queryClient.setQueryData(queryKeys.specRequirements.plReview(pjKey), review);
      await queryClient.invalidateQueries({ queryKey: queryKeys.specRequirements.list() });
    },
  });
}

/**
 * 成果物 1 つに PL の状態（確認中・承認・差し戻し）を付ける。
 *
 * PL かどうか・コメントの必須や上限・全成果物承認での自動承認は API が判定する。画面は返った
 * メッセージを見せるだけ。自動承認で案件全体の状態が変わりうるので、案件の一覧も読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 成果物ごとの PL の状態（確認中・承認・差し戻し）を記録する
 * @graph-connects /spec-requirements [calls] 成果物ごとの PL の状態提出API
 * @graph-connects specRequirementsApi.setPlArtifactStatus [calls] 成果物ごとの PL の状態提出APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSetSpecPlArtifactStatus(pjKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SpecPlArtifactSubmitInput & { artifact: string }) =>
      specRequirementsApi.setPlArtifactStatus(pjKey, input.artifact, {
        status: input.status,
        ...(input.comment === undefined ? {} : { comment: input.comment }),
      }),
    onSuccess: async (review) => {
      queryClient.setQueryData(queryKeys.specRequirements.plReview(pjKey), review);
      await queryClient.invalidateQueries({ queryKey: queryKeys.specRequirements.list() });
    },
  });
}
